import {useBreakpointDown} from '@travel-frontend/core';
import {IllustrationQuestions} from '@travel-frontend/illustrations';
import {Button, NoWrap, Stack} from '@travel-frontend/ui-components';
import {Suspense, lazy, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {ALTERNATIVE_HOTELS_CONTAINER} from '@/components/Recommendations/consts';
import {Container} from '@/modules/common/components/NoOffer/components/Container';
import {ControlsContainer} from '@/modules/common/components/NoOffer/components/ControlsContainer';
import {Text} from '@/modules/common/components/NoOffer/components/Text';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {useRecommendations} from '@/modules/common/hooks/useRecommendations';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';

const Recommendations = lazy(
  () => import('@/modules/common/components/Recommendations')
);

type Props = {
  hasFilters: boolean;
  small?: boolean;
  onClickClearFiltersButton: React.MouseEventHandler<HTMLButtonElement>;
};

const getOffset = (element: null | Element): number => {
  if (element instanceof HTMLElement) {
    return element.offsetTop + getOffset(element.offsetParent);
  }
  return 0;
};

export const NoOffer: React.FunctionComponent<Props> = ({
  hasFilters,
  small = false,
  onClickClearFiltersButton,
}) => {
  const {t} = useTranslation('filters');
  const mdDown = useBreakpointDown('md');
  const currentPage = useCurrentPage();
  const postFix = hasFilters ? 'withFilters' : 'default';
  const smDown = useBreakpointDown('sm');
  const [searchParams] = useSearchParams();
  const [filterParams] = useFilterParams();
  const {recommendations, isLoading} = useRecommendations({
    searchParams,
    filterParams,
  });
  const hasRecommendations = recommendations && recommendations.length > 0;
  const onClickScrollButton = useCallback(() => {
    let elementY = getOffset(
      document.getElementById(ALTERNATIVE_HOTELS_CONTAINER)
    );

    if (localStorage.getItem('hotlineBarFixedDismissedAt') === null) {
      elementY -= 50; // approx. sticky hotline height
    }

    window.scrollTo({
      top: elementY - 80, // approx. sticky searchbar height
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <>
      <Container smallLayout={mdDown || small}>
        <Stack direction="row" alignItems="center" spacing={smDown ? 0.75 : 2}>
          <IllustrationQuestions />
          <Text>{t(`noResults.${currentPage}.title.${postFix}`)}</Text>
        </Stack>
        <ControlsContainer>
          {hasFilters && (
            <Button category="secondary" onClick={onClickClearFiltersButton}>
              <NoWrap>{t('noResults.buttons.clearAllFilters')}</NoWrap>
            </Button>
          )}
          {hasRecommendations && (
            <Button
              category="primary"
              variant="interaction"
              onClick={onClickScrollButton}
            >
              <NoWrap>{t('noResults.buttons.showAlternatives')}</NoWrap>
            </Button>
          )}
        </ControlsContainer>
      </Container>
      <Suspense>
        <Recommendations
          recommendations={recommendations}
          isLoading={isLoading}
        />
      </Suspense>
    </>
  );
};
